import React, { useContext, useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import { createManageChannelBreadcrumbs } from '../Utils/BreadcrumbUtils';
import { Breadcrumb } from '../Components/Breadcrumb';
import DetailSection from '../Domain/DetailSection';
import { CREATE_CHANNEL, CREATE_CHANNEL_PERMISSION_MESSAGE, CREATE_PERMISSION, DELETE_PERMISSION, READ_PERMISSION, UPDATE_DOMAIN_PERMISSION_MESSAGE, UPDATE_PERMISSION } from '../Utils/Constants';
import ChannelTable from './ChannelTable';
import { CreateChannelDialog } from './CreateChannelDialog';
import { useNavigate } from "react-router";
import { TIndustry, TIndustryUpdatePayload } from '../Types/domain.types';
import { TChannel, TCreateParentChannelPayload } from '../Types/channel.types';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';
import Search from 'Components/DesignSystem/Search/Search';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import Pagination from 'Components/DesignSystem/Pagination/Pagination';
import EmptyState from 'Components/DesignSystem/EmptyState/EmptyState';
import NoDataIllustration from 'Components/DesignSystem/EmptyState/NoDataIllustration';
import PageSkeleton from 'Components/DesignSystem/PageSkeleton/PageSkeleton';
import { TPaginationParams } from 'Pages/GMS/Performance/analytics';
import { CreateDomainDialog } from '../Domain/CreateDomainDialog';
import { AppContext } from 'Context/AppContext';
import ServiceWrapper from 'Services/ServiceWrapper';
import AuthManager from 'Common/AuthManager';
import AppSettings from 'Settings/AppSettings';

const api_base_url = AppSettings.GMS_BACKEND_API_URL;

type TChannelProps = {
    partyId?: string;
    domainId?: string;
}

/** The two page sizes the legacy `<select>` offered, held to exactly those (§ 118). */
const PAGE_SIZE_OPTIONS = [
    { value: 10, label: '10' },
    { value: 20, label: '20' },
];

/** `CustomSearchBar`'s own default (`props.delay ?? 600`), preserved exactly. */
const SEARCH_DEBOUNCE_MS = 600;

/**
 * Channel management (`/admin/party/:partyId/domain/:domainId/channel`) on the design
 * system (§ 120). The chrome is the § 119 treatment applied to the third Admin list —
 * `Search`, `Button`, `Dropdown`, `Pagination`, `EmptyState` and the `summary-list`
 * skeleton preset — and `Breadcrumb` / `DetailSection` were already migrated in place
 * by § 119, so this screen inherited both.
 *
 * The load-bearing bit is the same as Manage Domain's: **`handleSearchChange` sets
 * `isChannelsLoading` true**, which is what keeps `ChannelTable` mounted through a
 * zero-result search. The create-first empty state is gated on
 * `!isChannelsLoading && channels.length === 0 && searchTerm === ''`, and the fetch
 * lives in the table's own effect — clear a zero-result search without that flag and
 * the parent swaps branches, unmounts the table, and takes the only thing that could
 * refetch with it (`okf/admin/e2e.md` § 5.2).
 *
 * `Create Channel` also collapses to ONE testid across both branches, as § 119 did for
 * domains: it was `party-create-channel-btn` in the empty state and
 * `channel-managechannel-button-1` in the toolbar, so a spec had to know which state
 * the page was in to click the same action.
 *
 * Behaviour deliberately unchanged (presentational scope): without
 * `gmsadministration_read` the effect redirects and returns *before*
 * `setIsLoading(false)`, so the skeleton runs forever behind the redirect; no dialog
 * validates a required field; and `Create Channel` is never `disabled` without create
 * permission — it refuses with a toast.
 *
 * Note the pagination gate here is `totalRecords > paginationParams.pageSize`, NOT
 * Manage Domain's `totalRecords > 0`. Left as-is: it is a behavioural difference
 * between two sibling screens, and reconciling it is a decision, not a migration.
 */
const ManageChannel = ({partyId, domainId}: TChannelProps) => {
    const navigate = useNavigate();
    const appContext = useContext(AppContext);
    
    const readPermission = appContext.pm.checkPermission(READ_PERMISSION);
    const createPermission = appContext.pm.checkPermission(CREATE_PERMISSION);
    const updatePermission = appContext.pm.checkPermission(UPDATE_PERMISSION);
    const deletePermission = appContext.pm.checkPermission(DELETE_PERMISSION);

    const [domainData, setDomainData] = useState<TIndustry | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isCreateChannelOpen, setIsCreateChannelOpen] = useState(false);
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [paginationParams, setPaginationParams] = useState<TPaginationParams>({pageNumber: 1, pageSize: 10});
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [searchTerm, setSearchTerm] = useState<string>('');
    /* The controlled input's own value; `searchTerm` is the debounced one the child's
       fetch effect keys on — `CustomSearchBar` held this half internally. */
    const [searchInput, setSearchInput] = useState<string>('');
    const [isCreatingChannel, setIsCreatingChannel] = useState(false);
    const [refreshChannelTable, setRefreshChannelTable] = useState(0);
    const [isEditDomainOpen, setIsEditDomainOpen] = useState(false);
    const [isUpdatingDomain, setIsUpdatingDomain] = useState(false);
    const [channels, setChannels] = useState<TChannel[]>([]);
    const [isChannelsLoading, setIsChannelsLoading] = useState(true);
    
    useEffect(() => {
        const fetchData = async () => {
            if (!partyId || !domainId) {
                setIsLoading(false);
                return;
            }
            try {
                setIsLoading(true);
                const response = await ServiceWrapper.doGet( `${api_base_url}party/${partyId}/industry/${domainId}`, null, null, AuthManager.getGmsToken(),); 
                const domainResponse = response?.data;
                if (domainResponse && domainResponse.done && domainResponse.data) {
                    setDomainData(domainResponse.data[0] ?? []);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                toast.error('Failed to load details');
            } finally {
                setIsLoading(false);
            }
        };
        if (!readPermission) {
            navigate("/workspace/module/permission/denied");
            return;
        } else {
            fetchData();
        }
    }, [partyId, domainId]);

    useEffect(() => {
        if (refreshChannelTable > 0) {
            setIsChannelsLoading(true);
        }
    }, [refreshChannelTable]);

    const handleNavigateToParties = () => {
        navigate('/admin/party/manage');
    };
    
    const handleNavigateToDomain = () => {
        if (partyId) {
            navigate(`/admin/party/domain/${partyId}`);
        }
    };    
    
    const breadcrumbItems =  domainData
        ? createManageChannelBreadcrumbs(domainData.party_name, domainData.name, handleNavigateToParties, handleNavigateToDomain)
        : [];

    const handleEditDomain = () => {
        if (!updatePermission) {
            toast.warning(UPDATE_DOMAIN_PERMISSION_MESSAGE);
            return;
        }  
        setIsEditDomainOpen(true);
    };

    const handleUpdateDomain = async (formData: any) => {
        if (!domainData || !domainId) return;
        
        setIsUpdatingDomain(true);
        
        try {
            const payload: TIndustryUpdatePayload = {
                active: true,
                description: formData.description || '',
                id: domainId,
                name: formData.domainName,
                sams_id: domainData.sams_id
            };
            
            const domainResponse = await ServiceWrapper.doPost( `${api_base_url}industry/edit/${domainId}`, payload, null, AuthManager.getGmsToken(),);
            const response = domainResponse?.data;
            if (response && response.done) {
                toast.success('Domain updated successfully!');
                
                setDomainData({
                    ...domainData,
                    name: formData.domainName,
                    description: formData.description || ''
                });
                
                setIsEditDomainOpen(false);
                await appContext.refreshHierarchy.refreshParties();
            } else {
                toast.error(response?.message || 'Failed to update domain');
            }
        } catch (error) {
            console.error('Error updating domain:', error);
            toast.error('An error occurred while updating the domain');
        } finally {
            setIsUpdatingDomain(false);
        }
    };

    const handleCreateChannel = async (formData: any) => {
        if (!createPermission) {
            toast.warning(CREATE_CHANNEL_PERMISSION_MESSAGE);
            return;
        }  
        if (!partyId || !domainId) return;
        
        setIsCreatingChannel(true);
        
        try {
            const payload: TCreateParentChannelPayload = {
                name: formData.channelName,
                party_id: partyId,
                industry_id: domainId,
                channel_ids: []
            };
            
            const channelResponse = await ServiceWrapper.doPost( `${api_base_url}parent_channel`, payload, null, AuthManager.getGmsToken(),);
            const response = channelResponse?.data;
            if (response && response.done) {
                toast.success('Channel created successfully!');
                setIsCreateChannelOpen(false);
                
                setRefreshChannelTable(prev => prev + 1);
                
                setPaginationParams(prev => ({ ...prev, pageNumber: 1 }));
                await appContext.refreshHierarchy.refreshParties();
            } else {
                toast.error(response?.message || 'Failed to create channel');
            }
        } catch (error) {
            console.error('Error creating channel:', error);
            toast.error('An error occurred while creating the channel');
        } finally {
            setIsCreatingChannel(false);
        }
    };

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setPaginationParams(prev => ({ ...prev, pageNumber: 1 }));
        // Keep ChannelTable mounted while the searched refetch runs: the fetch lives in
        // ChannelTable's useEffect, so if the empty-state branch unmounted it on a cleared
        // search (searchTerm '' + stale empty channels), nothing could ever refetch.
        setIsChannelsLoading(true);
    };

    /* `CustomSearchBar` built its debounce as `useCallback(debounce(...), [])`. Same
       shape, same 600ms, now cancelled on unmount. */
    const debouncedSearch = useMemo(
        () => debounce(handleSearchChange, SEARCH_DEBOUNCE_MS),
        []
    );

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    const onSearchInputChange = (value: string) => {
        /* `trimStart()` was `CustomSearchBar`'s, on both the visible and debounced
           values. */
        const next = value.trimStart();
        setSearchInput(next);
        debouncedSearch(next);
    };

    const handlePageChange = (pageNumber: number) => {
        setPaginationParams((prevState: TPaginationParams) => ({
            ...prevState,
            pageNumber: pageNumber,
        }));
    };
        
    const updatePageSize = (pageSize: number) => {
        setPaginationParams((prevState: TPaginationParams) => ({
            ...prevState,
            pageSize: pageSize,
            pageNumber: 1
        }));
    };

    if (isLoading) {
        /* `summary-list`, the preset added in § 119 for exactly this shape: breadcrumb →
           summary card → split toolbar → table → pagination. Not `detail`, which draws
           a form that never arrives here. */
        return <PageSkeleton preset="summary-list" label="Loading domain details" data-testid="channel-page-loading" />;
    }

    if (!domainData) {
        return (
            <EmptyState
                title="Data not found"
                size="md"
                data-testid="channel-domain-not-found"
            />
        );
    }

    const onCreateChannelClick = () => {
        if (!createPermission) {
            toast.warning(CREATE_CHANNEL_PERMISSION_MESSAGE);
            return;
        }
        setIsCreateChannelOpen(true);
    }

    return (
        <>
            {breadcrumbItems.length > 0 && <Breadcrumb items={breadcrumbItems} />}
            <DetailSection 
                config={{
                    type: 'domain',
                    data: {
                        title: domainData.name,
                        partyName: domainData.party_name,
                        description: domainData.description || 'N/A'
                    },
                    onEdit: handleEditDomain
                }}
            />
            {/* Create-first empty state only when the domain truly has no channels — a search
                with zero matches must keep the search bar mounted (so it can be cleared) and
                show ChannelTable's "No channels found" row instead. */}
            {!isChannelsLoading && channels.length === 0 && searchTerm === '' ? (
                <EmptyState
                    illustration={<NoDataIllustration />}
                    title="No Channel found"
                    size="md"
                    data-testid="channel-empty-state"
                    action={
                        <Button
                            variant="primary"
                            onClick={onCreateChannelClick}
                            data-testid="channel-create-btn"
                        >
                            {CREATE_CHANNEL}
                        </Button>
                    }
                />
            ) : (
                <>
                    {(channels.length !== 0 || searchTerm !== '') && (
                        <div className="tw-flex tw-items-center tw-justify-between tw-mt-2">
                            <div className="tw-w-[25%] tw-mt-4">
                                <Search
                                    value={searchInput}
                                    onChange={onSearchInputChange}
                                    /* § 288 — `showLoader` is the child table's fetch;
                                       the inequality is the 600ms debounce in front of
                                       it. Together they span the whole wait. */
                                    loading={showLoader || searchInput !== searchTerm}
                                    loadingLabel="Searching channels"
                                    placeholder="Search channels"
                                    ariaLabel="Search channels"
                                    size="md"
                                    data-testid="channel-search"
                                />
                            </div>
                            {/* Same testid as the empty-state button: only one branch is
                                ever mounted, so it still identifies exactly one element. */}
                            <Button
                                variant="primary"
                                onClick={onCreateChannelClick}
                                className="tw-w-max tw-whitespace-nowrap"
                                data-testid="channel-create-btn"
                            >
                                {CREATE_CHANNEL}
                            </Button>
                        </div>
                    )}
                    {partyId && domainId && (
                        <ChannelTable 
                            partyId={partyId}
                            domainId={domainId}
                            searchTerm={searchTerm}
                            showLoader={showLoader}
                            channels={channels}
                            setChannels={setChannels}
                            setShowLoader={setShowLoader}
                            setTotalRecords={setTotalRecords}
                            paginationParams={paginationParams}
                            refreshTrigger={refreshChannelTable}
                            onChannelsLoaded={() => setIsChannelsLoading(false)}
                            readPermission={readPermission}
                            createPermission={createPermission}
                            updatePermission={updatePermission}
                            deletePermission={deletePermission}
                        />
                    )}
                </>
            )}
            {!showLoader && channels.length !== 0 && totalRecords > paginationParams.pageSize ?
                <div id="pagination-container" className="tw-py-2">
                    <div className="tw-flex tw-justify-end tw-items-center tw-gap-3 tw-flex-wrap">
                        <div className="tw-flex tw-items-center tw-gap-2">
                            <Typography variant="body-primary" as="span" className="tw-text-ink-secondary">Show</Typography>
                            <Dropdown
                                label=""
                                aria-label="Records on page select"
                                size="sm"
                                className="tw-w-[84px]"
                                options={PAGE_SIZE_OPTIONS}
                                value={paginationParams?.pageSize ?? 10}
                                onChange={(value) => updatePageSize(Number(value))}
                                id="page-size-select"
                                data-testid="channel-page-size"
                            />
                        </div>
                        <Pagination
                            page={paginationParams?.pageNumber}
                            totalItems={totalRecords}
                            pageSize={paginationParams?.pageSize}
                            onPageChange={handlePageChange}
                            size="sm"
                            id="paginator"
                            data-testid="channel-pagination"
                        />
                    </div>
                </div> : null}
            <CreateChannelDialog 
                isOpen={isCreateChannelOpen}
                onClose={() => setIsCreateChannelOpen(false)}
                onSave={handleCreateChannel}
                isLoading={isCreatingChannel}
            />
            <CreateDomainDialog
                isOpen={isEditDomainOpen}
                onClose={() => setIsEditDomainOpen(false)}
                onSave={handleUpdateDomain}
                isLoading={isUpdatingDomain}
                editData={domainData}
                mode="edit"
            />
        </>
    );
}

export default ManageChannel;